/**
 * User Profile React Query Hook
 *
 * Fetches fresh user profile data from the SOKA API and syncs to Zustand for backward compatibility.
 * Used on landing page to ensure user points are fresh after game completion.
 */

import { useQuery } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';
import { authApi, type UserProfile } from '../services/auth.api';
import { useAuthStore } from '../stores/useAuthStore';

export const profileQueryKeys = {
  all: ['profile'] as const,
  detail: () => [...profileQueryKeys.all, 'detail'] as const
};

export function useUserProfile() {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);

  // Track last synced profile to prevent duplicate updates
  const lastSyncedProfileRef = useRef<UserProfile | null>(null);

  const query = useQuery({
    queryKey: profileQueryKeys.detail(),
    queryFn: () => authApi.getProfile(),
    enabled: isAuthenticated,
    refetchOnMount: true,
    staleTime: 1000 * 30 // 30 seconds
  });

  // Sync to Zustand on success for backward compatibility
  useEffect(() => {
    if (!query.data) return;

    const profileData = query.data;

    // Only sync if profile data actually changed
    const lastSynced = lastSyncedProfileRef.current;
    if (
      lastSynced &&
      lastSynced.points === profileData.points &&
      lastSynced.pseudo === profileData.pseudo &&
      lastSynced.country === profileData.country
    ) {
      return;
    }

    // Get current user from store (avoid closure staleness)
    const currentUser = useAuthStore.getState().user;
    if (!currentUser) return;

    lastSyncedProfileRef.current = profileData;
    useAuthStore.getState().setUser({
      ...currentUser,
      points: profileData.points,
      pseudo: profileData.pseudo,
      country: profileData.country
    });
  }, [query.data]);

  return query;
}
